// ============================================================
// DEMO SCENARIOS - Pure fixture builders for the dev-mode demo
// scenario loader (?demo=<name>). Lets a human test the app
// without manual data entry. Dev/emulator builds only - the
// caller passes `enabled` so this module stays env-agnostic.
// ============================================================
import type { Slot, Tab, Target } from '../types';
import type { Character, AttackerCombatStats } from './character/types';
import { emptySlot, emptyTarget, blankProfile, createTab, characterToTarget } from './factories';
import { emptyCharacter, emptyWeapon } from './character/factories';
import { resolveAttackerStats } from './character/combatStatsResolver';

export interface DemoScenario {
  name: string;
  description: string;
  atkProfiles: {
    current: string;
    list: Record<string, Slot[]>;
    /** Character-as-attacker bindings - hydrated by DeathWatchSimulator's binding effect. */
    _charBindings?: Record<string, { stats: AttackerCombatStats; character: Character }>;
  };
  tabs: Tab[];
  activeTab: string;
  /**
   * House rules the scenario wants on. Seeded ONCE by DeathWatchSimulator,
   * and only while the GM has never set the key - a demo may not overrule
   * a real ruling. Demos are the only thing here that can ask for a rule,
   * because they exist to remove manual setup.
   */
  houseRules?: { roundCountdown?: boolean };
}

export const DEMO_SCENARIO_NAMES = ['full-combat', 'horde', 'crits'] as const;
type DemoScenarioName = (typeof DEMO_SCENARIO_NAMES)[number];

function demoSlot(index: number, overrides: Partial<Slot>): Slot {
  return { ...emptySlot(index), ...overrides, isEmpty: false };
}

function demoTarget(overrides: Partial<Target>): Target {
  return { ...emptyTarget(), ...overrides };
}

// Declarative: one literal per demo weapon, so its length is data, not logic.
function demoWeapons(): Slot[] {
  const slots = blankProfile();
  slots[0] = demoSlot(0, {
    name: 'Bolter',
    weaponName: 'Godwyn Bolter',
    weaponType: 'ranged',
    damageExpression: '1d10+9',
    damageType: 'X',
    penetration: 4,
    range: 100,
    rateOfFire: { single: true, semi: 3, full: 0 },
    skillValue: 55,
    righteousFury: true,
    specialRules: ['Tearing'],
    ammo: { current: 28, max: 28, reloadAction: 'Full' },
  });
  slots[1] = demoSlot(1, {
    name: 'Chainsword',
    weaponName: 'Astartes Chainsword',
    weaponType: 'melee',
    damageExpression: '1d10+3',
    damageType: 'R',
    penetration: 3,
    skillValue: 50,
    strengthBonus: 8,
    canSwiftAttack: true,
    canLightningAttack: true,
    specialRules: ['Tearing', 'Balanced'],
  });
  slots[2] = demoSlot(2, {
    name: 'Plasma Gun',
    weaponName: 'Ragefire Plasma Gun',
    weaponType: 'ranged',
    damageExpression: '1d10+7',
    damageType: 'E',
    penetration: 6,
    range: 90,
    rateOfFire: { single: true, semi: 2, full: 0 },
    skillValue: 55,
    specialRules: ['Volatile'],
    ammo: { current: 24, max: 24, reloadAction: 'Full' },
  });
  slots[3] = demoSlot(3, {
    name: 'Heavy Bolter',
    weaponName: 'Heavy Bolter',
    weaponType: 'ranged',
    damageExpression: '1d10+12',
    damageType: 'X',
    penetration: 5,
    range: 150,
    rateOfFire: { single: false, semi: 0, full: 6 },
    skillValue: 50,
    specialRules: ['Tearing'],
    ammo: { current: 60, max: 60, reloadAction: 'Full' },
  });
  return slots;
}

// ---- Demo characters (full-combat only) ----
// Bound alongside the hand-tuned slots so the character-driven features
// (Overview action sheets, talents/traits lists, unnatural multipliers,
// ×N badge overrides) are one click from testable. Values are chosen so
// combat numbers stay IDENTICAL to the pre-character demo: Str 40 ×2
// gives SB 8 (the chainsword slot's existing strengthBonus) and the
// bound talents add no damage or skill bonuses. demo-smoke's seeded
// dice therefore keep working unchanged.

// Declarative: the demo Marine's sheet as one literal - length is data, not logic.
function demoMarine(): Character {
  const base = emptyCharacter('demo-marine');
  return {
    ...base,
    name: 'Brother Castiel',
    characteristics: {
      ws: { value: 50, unnaturalMultiplier: 1 },
      bs: { value: 55, unnaturalMultiplier: 1 },
      str: { value: 40, unnaturalMultiplier: 2 },
      tgh: { value: 45, unnaturalMultiplier: 1 },
      agi: { value: 45, unnaturalMultiplier: 1 },
      int: { value: 40, unnaturalMultiplier: 1 },
      per: { value: 42, unnaturalMultiplier: 2 },
      wp: { value: 45, unnaturalMultiplier: 1 },
      fel: { value: 40, unnaturalMultiplier: 1 },
    },
    talents: [
      { talentId: 'swift_attack' },
      { talentId: 'lightning_attack' },
      { talentId: 'sure_strike' },
      { talentId: 'hunter_of_aliens' },
    ],
    skills: {
      dodge: { trained: true, plus10: false, plus20: false, mod: 0, bonus: 0 },
      parry: { trained: true, plus10: false, plus20: false, mod: 10, bonus: 10 },
    },
    weapons: [
      {
        ...emptyWeapon('demo-w1'),
        name: 'Godwyn Bolter',
        type: 'ranged',
        damageExpression: '1d10+9',
        damageType: 'X',
        penetration: 4,
        range: 100,
        rateOfFire: { single: true, semi: 3, full: 0 },
        specialRules: ['Tearing'],
      },
      {
        ...emptyWeapon('demo-w2'),
        name: 'Astartes Chainsword',
        type: 'melee',
        damageExpression: '1d10+3',
        damageType: 'R',
        penetration: 3,
        specialRules: ['Tearing', 'Balanced'],
      },
    ],
    wounds: { current: 22, max: 22 },
    movement: { half: 4, full: 8, charge: 12, run: 24 },
  };
}

// Declarative: the demo Tyranid's sheet as one literal - length is data, not logic.
function demoTyranid(): Character {
  const base = emptyCharacter('demo-tyranid');
  return {
    ...base,
    name: 'Tyranid Warrior',
    // Xenos: showcases the Deathwatch-Training RF auto-confirm (inert unless
    // the attacker has the talent, so seeded demo numbers are untouched)
    xenos: true,
    characteristics: {
      ws: { value: 55, unnaturalMultiplier: 1 },
      bs: { value: 30, unnaturalMultiplier: 1 },
      str: { value: 45, unnaturalMultiplier: 2 },
      tgh: { value: 45, unnaturalMultiplier: 2 },
      agi: { value: 40, unnaturalMultiplier: 2 },
      int: { value: 25, unnaturalMultiplier: 1 },
      per: { value: 40, unnaturalMultiplier: 1 },
      wp: { value: 40, unnaturalMultiplier: 1 },
      fel: { value: 10, unnaturalMultiplier: 1 },
    },
    traits: [
      { traitId: 'fear', parameter: '2' },
      { traitId: 'natural_armour', parameter: '3' },
      { traitId: 'from_beyond' },
    ],
    skills: {
      dodge: { trained: true, plus10: false, plus20: false, mod: 0, bonus: 0 },
      parry: { trained: false, plus10: false, plus20: false, mod: 0, bonus: 0 },
    },
    wounds: { current: 24, max: 24 },
    movement: { half: 5, full: 10, charge: 15, run: 30 },
  };
}

const BUILDERS: Record<DemoScenarioName, () => DemoScenario> = {
  'full-combat': () => {
    const marine = demoMarine();
    const tyranid = demoTyranid();
    return {
      name: 'full-combat',
      description:
        'Loaded Deathwatch armoury (character-bound) vs four varied targets (dodge, parry, conditions, countdown, unnatural monster).',
      atkProfiles: {
        current: 'Demo Loadout',
        list: { 'Demo Loadout': demoWeapons() },
        _charBindings: {
          'Demo Loadout': { stats: resolveAttackerStats(marine, 'demo-w1'), character: marine },
        },
      },
      tabs: [
        createTab(
          demoTarget({
            name: 'Ork Boy',
            wounds: { current: 12, max: 12 },
            toughnessBonus: 4,
            unnaturalToughness: 0,
            dodge: { canDodge: true, agility: 35, additionalDodges: 0 },
          }),
          'demo-tab-1',
        ),
        createTab(
          demoTarget({
            name: 'Chaos Space Marine',
            wounds: { current: 20, max: 20 },
            toughnessBonus: 4,
            unnaturalToughness: 1,
            dodge: { canDodge: true, agility: 40, additionalDodges: 0 },
            parry: {
              canParry: true,
              weaponSkill: 45,
              hasPowerField: false,
              additionalParries: 0,
              canParryRanged: false,
            },
            astartes: true,
            size: 'Hulking',
          }),
          'demo-tab-2',
        ),
        createTab(
          demoTarget({
            name: 'Stunned Cultist',
            wounds: { current: 8, max: 8 },
            toughnessBonus: 3,
            unnaturalToughness: 0,
            conditions: ['S2'],
          }),
          'demo-tab-3',
        ),
        // Character-spawned monster: exercises the defender action sheet,
        // traits text list, and the unnatural DoS/multiplier features
        createTab(characterToTarget(tyranid), 'demo-tab-4', tyranid),
      ],
      activeTab: 'demo-tab-1',
      // The Stunned Cultist is here to show conditions ticking down.
      houseRules: { roundCountdown: true },
    };
  },
  horde: () => ({
    name: 'horde',
    description: 'Ork mob horde (magnitude 40) for horde-damage and magnitude-loss testing.',
    atkProfiles: { current: 'Demo Loadout', list: { 'Demo Loadout': demoWeapons() } },
    tabs: [
      createTab(
        demoTarget({
          name: 'Ork Mob',
          bodyType: 'horde',
          toughnessBonus: 4,
          horde: { isHorde: true, magnitude: 40, maxMagnitude: 40 },
        }),
        'demo-tab-1',
      ),
    ],
    activeTab: 'demo-tab-1',
  }),
  crits: () => ({
    name: 'crits',
    description: 'Fragile target (5 wounds, no armour) so critical damage tables trigger immediately.',
    atkProfiles: { current: 'Demo Loadout', list: { 'Demo Loadout': demoWeapons() } },
    tabs: [
      createTab(
        demoTarget({
          name: 'Grot',
          wounds: { current: 5, max: 5 },
          toughnessBonus: 2,
          unnaturalToughness: 0,
          armour: { head: 0, body: 0, leftArm: 0, rightArm: 0, leftLeg: 0, rightLeg: 0 },
          size: 'Scrawny',
        }),
        'demo-tab-1',
      ),
    ],
    activeTab: 'demo-tab-1',
  }),
};

/** Extract the demo scenario name from a location.search string, or null. */
export function parseDemoParam(search: string): string | null {
  const value = new URLSearchParams(search).get('demo');
  return value && value.trim() !== '' ? value.trim() : null;
}

/**
 * Resolve a demo scenario from location.search. Returns null when
 * disabled (production), when no ?demo= param is present, or when
 * the name is unknown.
 */
export function getDemoScenario(search: string, enabled: boolean): DemoScenario | null {
  if (!enabled) return null;
  const name = parseDemoParam(search);
  if (!name || !(name in BUILDERS)) return null;
  return BUILDERS[name as DemoScenarioName]();
}
